/**
 * PostAddPrompt — Together? prompt shown after adding an item from search.
 *
 * Solo users (no pod / no other members) get onDone(true) fired immediately.
 * Pod users see the TogetherPrompt to log a co-activity.
 *
 * @param {Object} props.addedItem - The item that was just added
 * @param {string} props.itemType - 'movie' | 'game' | 'book' | 'boardgame'
 * @param {string} props.activityType - 'watched' | 'played' | 'read' | 'finished'
 * @param {Function} props.onDone - (navigateToItem: boolean) => void
 * @param {Object} [props.backendService] - Unused, kept for backwards compat
 * @param {string} [props.activeView] - Unused, kept for backwards compat
 */
import { useRef, useEffect } from 'react';
import TogetherPrompt from './TogetherPrompt.jsx';
import { usePod } from '../hooks/usePod.jsx';
import { useCurrentUser } from '../hooks/useCurrentUser.jsx';
import ActivityService from '../services/ActivityService.js';

export default function PostAddPrompt({ addedItem, itemType, activityType, onDone, backendService, activeView }) {
    const { pod, allMembers } = usePod();
    const currentUser = useCurrentUser();
    const serviceRef = useRef(null);
    const doneRef = useRef(false);

    if (!serviceRef.current && currentUser?.id) {
        serviceRef.current = new ActivityService(currentUser.id);
    }

    const otherMembers = allMembers?.filter((m) => m.userId !== currentUser?.id) || [];
    const shouldSkipTogether = !pod || otherMembers.length === 0;

    useEffect(() => {
        if (shouldSkipTogether && !doneRef.current) {
            doneRef.current = true;
            onDone(true);
        }
    }, [shouldSkipTogether]); // eslint-disable-line react-hooks/exhaustive-deps

    if (shouldSkipTogether) return null;

    const handleJustMe = () => onDone(true);

    const handleTogether = async (podId, selectedUserIds) => {
        if (serviceRef.current && currentUser) {
            try {
                await serviceRef.current.create({
                    type: activityType,
                    itemType,
                    itemId: addedItem.id,
                    itemTitle: addedItem.title || '',
                    participants: [currentUser.id, ...selectedUserIds],
                    pod: podId,
                    date: new Date().toISOString(),
                });
            } catch (err) {
                console.error('Failed to create activity:', err);
            }
        }
        onDone(true);
    };

    const promptMembers = otherMembers.map((m) => ({
        userId: m.userId,
        displayName: m.displayName || m.userName,
        emoji: m.emoji || '👤',
    }));

    return (
        <TogetherPrompt
            itemTitle={addedItem.title || 'Item'}
            members={promptMembers}
            podId={pod.id}
            onJustMe={handleJustMe}
            onTogether={handleTogether}
        />
    );
}
